// src/admin-components/AdminFeedback.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Topbar  from "./Topbar";
import { adminGetFeedbackApi, adminGetServicesApi } from "../api/bookingApi";
import "./admin-dashboard.css";

// ── Star display ──────────────────────────────────────────
function Stars({ rating }) {
  return (
    <span>
      {[1,2,3,4,5].map(s => (
        <span key={s} style={{ color: s <= rating ? "#F59E0B" : "#E2E8F0", fontSize: 16 }}>★</span>
      ))}
    </span>
  );
}

// ── Rating bar ────────────────────────────────────────────
function RatingBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
      <span style={{ fontSize:13, fontWeight:700, width:60, color:"#64748B" }}>{label}</span>
      <div style={{ flex:1, height:10, background:"#E2E8F0", borderRadius:999, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:999, transition:"width .4s" }}/>
      </div>
      <span style={{ fontSize:13, fontWeight:700, width:30, textAlign:"right" }}>{count}</span>
      <span style={{ fontSize:12, color:"#94A3B8", width:36 }}>{pct}%</span>
    </div>
  );
}

export default function AdminFeedback() {
  const [feedback,  setFeedback]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filters,   setFilters]   = useState({ service_id:"", rating:"", search:"" });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.service_id) params.service_id = f.service_id;
      if (f.rating)     params.rating     = f.rating;
      if (f.search)     params.search     = f.search;

      const [fbRes, svcRes] = await Promise.all([
        adminGetFeedbackApi(params),
        adminGetServicesApi(),
      ]);
      setFeedback(fbRes.data.feedback  || []);
      setStats(fbRes.data.stats);
      setServices(svcRes.data.services || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setFilter = (k, v) => {
    const next = { ...filters, [k]: v };
    setFilters(next);
    load(next);
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", {
    day:"numeric", month:"short", year:"numeric"
  }) : "—";

  const STAR_COLORS = { 5:"#16A34A", 4:"#65A30D", 3:"#D97706", 2:"#EA580C", 1:"#DC2626" };

  return (
    <div className="adm-layout">
      <Sidebar />
      <main className="adm-main">
        <div className="adm-shell">
          <Topbar title="Customer Feedback" subtitle="Reviews & ratings for all services" />

          {/* ── STATS ROW ── */}
          {stats && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>

              {/* Average rating card */}
              <div style={{
                background:"#fff", border:"1px solid #E2E8F0", borderRadius:18,
                padding:"24px 28px", display:"flex", alignItems:"center", gap:24
              }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:52, fontWeight:900, color:"#F59E0B", lineHeight:1 }}>
                    {stats.avg_rating || "—"}
                  </div>
                  <div style={{ display:"flex", justifyContent:"center", gap:2, margin:"8px 0 4px" }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{
                        fontSize:22,
                        color: s <= Math.round(stats.avg_rating) ? "#F59E0B" : "#E2E8F0"
                      }}>★</span>
                    ))}
                  </div>
                  <div style={{ fontSize:13, color:"#94A3B8" }}>{stats.total} reviews</div>
                </div>
                <div style={{ flex:1 }}>
                  <RatingBar label="5 ★" count={stats.five_star}  total={stats.total} color="#16A34A" />
                  <RatingBar label="4 ★" count={stats.four_star}  total={stats.total} color="#65A30D" />
                  <RatingBar label="3 ★" count={stats.three_star} total={stats.total} color="#D97706" />
                  <RatingBar label="2 ★" count={stats.two_star}   total={stats.total} color="#EA580C" />
                  <RatingBar label="1 ★" count={stats.one_star}   total={stats.total} color="#DC2626" />
                </div>
              </div>

              {/* Quick stats */}
              <div style={{
                display:"grid", gridTemplateColumns:"1fr 1fr", gap:12
              }}>
                {[
                  { icon:"📝", label:"Total Reviews",   val: stats.total,     bg:"#EFF6FF", c:"#1D4ED8" },
                  { icon:"⭐", label:"Average Rating",   val: stats.avg_rating || "N/A", bg:"#FFFBEB", c:"#92400E" },
                  { icon:"🏆", label:"5-Star Reviews",   val: stats.five_star,  bg:"#F0FDF4", c:"#15803D" },
                  { icon:"⚠️", label:"1-Star Reviews",   val: stats.one_star,   bg:"#FEF2F2", c:"#DC2626" },
                ].map((k,i) => (
                  <div key={i} style={{
                    background:k.bg, border:`1px solid ${k.c}22`,
                    borderRadius:14, padding:"16px 18px"
                  }}>
                    <div style={{ fontSize:24, marginBottom:4 }}>{k.icon}</div>
                    <div style={{ fontSize:24, fontWeight:800, color:k.c }}>{k.val}</div>
                    <div style={{ fontSize:12, color:"#64748B", fontWeight:600 }}>{k.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── FILTERS ── */}
          <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
            <input
              value={filters.search}
              onChange={e => setFilter("search", e.target.value)}
              placeholder="Search user, service, comment..."
              style={{
                padding:"9px 14px", borderRadius:10, border:"1px solid #CBD5E1",
                fontSize:14, flex:1, minWidth:220, outline:"none"
              }}
            />
            <select
              value={filters.service_id}
              onChange={e => setFilter("service_id", e.target.value)}
              style={{
                padding:"9px 14px", borderRadius:10, border:"1px solid #CBD5E1",
                fontSize:14, background:"#fff", cursor:"pointer", minWidth:180
              }}
            >
              <option value="">All Services</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              value={filters.rating}
              onChange={e => setFilter("rating", e.target.value)}
              style={{
                padding:"9px 14px", borderRadius:10, border:"1px solid #CBD5E1",
                fontSize:14, background:"#fff", cursor:"pointer", minWidth:140
              }}
            >
              <option value="">All Ratings</option>
              {[5,4,3,2,1].map(r => (
                <option key={r} value={r}>{"★".repeat(r)} ({r} star)</option>
              ))}
            </select>
            {(filters.search || filters.service_id || filters.rating) && (
              <button
                onClick={() => { setFilters({ service_id:"", rating:"", search:"" }); load({ service_id:"", rating:"", search:"" }); }}
                style={{
                  padding:"9px 14px", borderRadius:10, border:"1px solid #FCA5A5",
                  background:"#FEF2F2", color:"#DC2626", cursor:"pointer",
                  fontWeight:700, fontSize:13
                }}
              >✕ Clear</button>
            )}
          </div>

          {/* ── FEEDBACK LIST ── */}
          <div className="adm-panel" style={{ gridColumn:"span 12" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>
                All Feedback
                <span style={{ fontSize:13, color:"#94A3B8", fontWeight:400, marginLeft:8 }}>
                  ({feedback.length} {feedback.length === 1 ? "review" : "reviews"})
                </span>
              </h3>
            </div>

            {loading ? (
              <div style={{ padding:40, textAlign:"center", color:"#94A3B8" }}>Loading feedback...</div>
            ) : feedback.length === 0 ? (
              <div style={{ padding:60, textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>💬</div>
                <div style={{ fontSize:16, fontWeight:700, color:"#1E293B", marginBottom:6 }}>
                  No feedback yet
                </div>
                <div style={{ fontSize:14, color:"#94A3B8" }}>
                  Feedback will appear here once users complete their bookings and leave reviews.
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                {feedback.map(f => (
                  <div key={f.id} style={{
                    border:"1px solid #E2E8F0", borderRadius:16, padding:"20px 22px",
                    background:"#fff",
                    borderLeft:`4px solid ${STAR_COLORS[f.rating] || "#94A3B8"}`
                  }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>

                      {/* Left — user info */}
                      <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                        <div style={{
                          width:44, height:44, borderRadius:"50%", flexShrink:0,
                          background:"#1F4E8C", color:"#fff",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:18, fontWeight:800
                        }}>
                          {f.user_name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                          <div style={{ fontWeight:800, fontSize:15, color:"#1E293B" }}>{f.user_name}</div>
                          <div style={{ fontSize:13, color:"#64748B" }}>{f.user_phone}</div>
                        </div>
                      </div>

                      {/* Right — rating + date */}
                      <div style={{ textAlign:"right" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                          <Stars rating={f.rating} />
                          <span style={{
                            fontSize:12, fontWeight:800, padding:"2px 8px", borderRadius:999,
                            background: f.rating >= 4 ? "#DCFCE7" : f.rating === 3 ? "#FEF9C3" : "#FEE2E2",
                            color: f.rating >= 4 ? "#16A34A" : f.rating === 3 ? "#92400E" : "#DC2626"
                          }}>{f.rating}/5</span>
                        </div>
                        <div style={{ fontSize:12, color:"#94A3B8", marginTop:4 }}>{fmt(f.created_at)}</div>
                      </div>
                    </div>

                    {/* Service + booking info */}
                    <div style={{ display:"flex", gap:8, margin:"12px 0 10px", flexWrap:"wrap" }}>
                      <span style={{
                        fontSize:12, fontWeight:700, padding:"4px 10px", borderRadius:999,
                        background:"#EFF6FF", color:"#1D4ED8"
                      }}>🛠 {f.service_name}</span>
                      <span style={{
                        fontSize:12, fontWeight:700, padding:"4px 10px", borderRadius:999,
                        background:"#F1F5F9", color:"#475569"
                      }}>📋 Booking #{f.booking_id}</span>
                      <span style={{
                        fontSize:12, fontWeight:700, padding:"4px 10px", borderRadius:999,
                        background:"#F1F5F9", color:"#475569"
                      }}>📅 {fmt(f.booking_date)}</span>
                    </div>

                    {/* Comment */}
                    {f.comment ? (
                      <div style={{
                        background:"#F8FAFC", border:"1px solid #E2E8F0",
                        borderRadius:10, padding:"12px 14px",
                        fontSize:14, color:"#374151", lineHeight:1.6,
                        fontStyle:"italic"
                      }}>
                        "{f.comment}"
                      </div>
                    ) : (
                      <div style={{ fontSize:13, color:"#CBD5E1", fontStyle:"italic" }}>
                        No written comment
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}